import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from "react-router-dom";

import cx from 'classnames';
import _ from 'lodash';
import Truncate from "react-truncate";

import {
  fetchStoryboardVisitorsReport, fetchStoryboardSessionsTimeSeries
} from 'actions/storyboardReports';
import { fetchAnalyticsChartForInteractive } from 'actions/interactiveReports';
import { fetchAnalyticsChartForLandingPage } from 'actions/landingPageReports';

import { TrendBarChart, TrendsLineChart } from './charts'

import { convertDatesToTimezone } from 'services/report.service';
import { EmptyMessage, getWidthForAnalyticsBar, LoadingMessage, SessionAnalyticsHeader, TableInfo, VisitorProfileView } from '../components/index'
import { TableFooter } from './tableFooter';
import { useReportType } from '../ReportsProvider';
import { formatDurationWithLabel } from '../reporthelpers';

import styles from '../report.module.scss';

export function SessionAnalytics(props) {
  const dispatch = useDispatch();

  let { storyboardId } = useParams();
  const visitors = useSelector(state => _.get(state, 'storyboards.storyboard.reports.visitors.items'));
  const views = useSelector(state => _.get(state, 'storyboards.storyboard.reports.sessionTimeSeries.items'));
  const pagination = useSelector(state => _.get(state, 'storyboards.storyboard.reports.visitors.pagination'));
  const loading = useSelector(state => _.get(state, 'storyboards.storyboard.reports.visitors.loading'));
  const [currentChart, setCurrentChart] = useState("table_view");
  const { reportType } = useReportType()

  useEffect(() => {
    if (props.date) {
      handlePageChange(0);
    }
  }, [props.date, reportType]);

  const handlePageChange = (page) => {
    var request = {
      storyboard: storyboardId,
      reportType,
      ...convertDatesToTimezone(props.date),
    }

    dispatch(fetchStoryboardVisitorsReport(request, page, 5));
    dispatch(fetchStoryboardSessionsTimeSeries(request));
  }

  const handleChartChange = (value) => {
    setCurrentChart(value)
  }

  return (
    <div className={styles.sessionAnalyticsWrapper}>
      <div className={styles.sessionAnalyticsContainer}>
        <TableInfo
          name="Session Analytics"
          pagination={pagination} />

        {currentChart == "table_view" && <SessionAnalyticsHeader />}

        {currentChart == "table_view" &&
          !loading && visitors && visitors.map((data, index) => {
            return <VisitorReportRow storyboard={storyboardId} data={data} index={index} storyboardType={props.storyboardType} date={props.date}/>
          })
        }

        {currentChart == "graph_view" &&
          <div className={cx(styles.graphFullView, {[styles.emptyGraph]: (views && views.length == 0)})}>
            <TrendsLineChart height={400} data={views} dataKey="views" loading={loading} />
          </div>
        }

        {loading &&
          <LoadingMessage />
        }

        {(visitors && visitors.length == 0) && !loading &&
          <EmptyMessage />
        }
      </div>

      <TableFooter
        currentChart={currentChart}
        disableView={['pie_chart_view']}
        handleChartChange={handleChartChange}
        handlePageChange={handlePageChange}
        pagination={pagination}
        paginationName={"Sessions"}
      />
    </div>
  )
}

function VisitorReportRow({ index, storyboard, data, storyboardType, date }) {
  const dispatch = useDispatch();
  const sessionsAnalyticsData = useSelector(state => _.get(state, `storyboards.storyboard.reports.analytics.sessions.items[${data.visitorSession}]`));
  const { reportType } = useReportType()
  useEffect(() => {
    if (data.visitorSession) {
      var request = {
        storyboard: storyboard,
        visitorSession: data.visitorSession,
        reportType,
        ...convertDatesToTimezone(date),
      }

      if(storyboardType == "INTERACTIVE_CONTENT") {
        dispatch(fetchAnalyticsChartForInteractive(request, "sessions"));
      }
      
      if(storyboardType == "LANDING_PAGE") {
        dispatch(fetchAnalyticsChartForLandingPage(request, "sessions"))
      }
    }
  }, [data.visitorSession, reportType]);

  if (!data || data.length <= 1) {
    return false;
  }

  const renderLocation = () => {
    if (!_.get(data, 'country.city')) {
      return 'UNKNOWN';
    } else {
      return `${_.get(data, 'country.city') || 'UNKNOWN'}, ${_.get(data, 'country.region_name')}, ${_.get(data, 'country.country_code')}`
    }
  }

  const capitalizeFirstLetter = (value) => {
    if (value) {
      value = value.charAt(0).toUpperCase() + value.slice(1);
    }

    return value;
  }

  const renderSource = () => {
    var sourceAndMedium = "-";

    if(data.campaign != null) {
      var source  = data.campaign.source ? `${capitalizeFirstLetter(data.campaign.source)}` : "";
      var medium  = data.campaign.medium ? `${capitalizeFirstLetter(data.campaign.medium)}` : "";

      if(source) {
        sourceAndMedium = source;
      }

      if(medium) {
        sourceAndMedium = medium;
      }

      if(source && medium) {
        sourceAndMedium = source.concat(`, ${medium}`);
      }
    }

    return sourceAndMedium;
  }

  return (
    <div className={styles.sessionAnalyticsRow}>
      <VisitorProfileView
        data={data}
        type="sessions"
      />

      <div className={cx(styles.wrapper, styles.sourceColumn)}>
        <div className={styles.headline}>
          <Truncate width={315} lines={1}>
          {renderSource()}
          </Truncate>
        </div>
        <div className={styles.desription}>
          <Truncate width={315} lines={1}>
            {data.url}
          </Truncate>
        </div>
      </div>


      <div className={cx(styles.wrapper, styles.locationColumn)}>
        <div className={styles.headline}>{renderLocation()}</div>
        <div className={styles.description}>{data.browser} on {data.device}</div>
      </div>

      <div className={cx(styles.wrapper, styles.timespentColumn)}>
        <div className={styles.headline}>{formatDurationWithLabel(data.timeSpent).value}</div>
        <div className={styles.description}>{`${data.completion}%`}</div>
      </div>

      <div className={cx(styles.wrapper, styles.pageAnalyticsView)}>
        {sessionsAnalyticsData && sessionsAnalyticsData.length > 0 ?
          <TrendBarChart data={sessionsAnalyticsData} width={getWidthForAnalyticsBar(sessionsAnalyticsData)} dataKey="views"/> : "-"
        }
      </div>
    </div>
  )
}
